import { Box, Button, IconButton, Stack, Typography } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { PromQLEditor } from '@chronosphereio/editors/prometheus';
import { produce } from 'immer';
import { DeleteOutline as DeleteIcon } from 'mdi-material-ui';
import { useCallback, useRef, useState } from 'react';
import { usePrometheusClient } from '../model';
import { CodeBuilderToggle } from './prometheus-time-series-query/PrometheusTimeSeriesQueryEditor';
import { PromQLBuilder } from './prometheus-time-series-query/promql-builder';

type MatcherEditorProps = {
  matchers: string[];
  onChange: (matchers: string[]) => void;
  isReadonly?: boolean;
};

/**
 * Matcher editor for Prometheus variables.
 */
export function MatcherEditor({ matchers, onChange, isReadonly }: MatcherEditorProps) {
  // We should only allow for one matcher so if there are any matchers we should not show the add button.
  // Keeps UX simple even though API does support multiple matchers.
  const showAddMatcher = matchers.length === 0;
  return (
    <Stack spacing={1} mb={2}>
      {matchers.map((matcher, index) => (
        <MatcherRow
          key={index}
          defaultValue={matcher}
          isReadonly={isReadonly}
          onCommit={(value) => {
            const newMatchers = produce(matchers, (draft) => {
              draft[index] = value;
            });
            onChange(newMatchers);
          }}
          onDelete={() => {
            const newMatchers = produce(matchers, (draft) => {
              draft.splice(index, 1);
            });
            onChange(newMatchers);
          }}
        />
      ))}
      {showAddMatcher && (
        <Button
          fullWidth={false}
          variant="secondary"
          sx={{ alignSelf: 'start' }}
          onClick={() => {
            const newMatchers = produce(matchers, (draft) => {
              draft.push('');
            });
            onChange(newMatchers);
          }}
          disabled={isReadonly}
        >
          Add series selector
        </Button>
      )}
    </Stack>
  );
}

type MatcherRowProps = {
  defaultValue: string;
  isReadonly?: boolean;
  onCommit: (value: string) => void;
  onDelete: () => void;
};

/**
 * Uncontrolled matcher input, the component owns its own draft state and only
 * propagates changes to the parent on blur for better performance.
 */
function MatcherRow({ defaultValue, isReadonly, onCommit, onDelete }: MatcherRowProps) {
  const client = usePrometheusClient();
  const promURL = client?.options.datasourceUrl;
  const { devErgoSeriesSelectorBuilderKillSwitch } = useFeatureFlags();

  const [mode, setMode] = useState<'code' | 'builder'>('code');
  const showToggle = !devErgoSeriesSelectorBuilderKillSwitch && !isReadonly;
  const showBuilder = showToggle && mode === 'builder';
  const [localValue, setLocalValue] = useState(defaultValue);
  // Use a ref so the onBlur callback always sees the latest value without
  // needing to be recreated (which would cause PromQLEditor to remount).
  const localValueRef = useRef(localValue);

  // Sync local state when defaultValue changes externally
  const [prevDefaultValue, setPrevDefaultValue] = useState(defaultValue);
  if (defaultValue !== prevDefaultValue) {
    setPrevDefaultValue(defaultValue);
    setLocalValue(defaultValue);
    localValueRef.current = defaultValue;
  }

  const handleChange = useCallback((value: string) => {
    setLocalValue(value);
    localValueRef.current = value;
  }, []);

  const handleBlur = useCallback(() => {
    onCommit(localValueRef.current);
  }, [onCommit]);

  const handleBuilderChange = useCallback(
    (query: string) => {
      setLocalValue(query);
      localValueRef.current = query;
      onCommit(query);
    },
    [onCommit]
  );

  return (
    <Stack spacing={1}>
      <Typography>Series selector</Typography>
      <Typography variant="caption" color="text.secondary">
        {
          'Filter variable values to those present in matching series, e.g. metric_name{label="value"} (no functions or aggregations)'
        }
      </Typography>
      {showToggle ? <CodeBuilderToggle query={localValue} mode={mode} onModeChange={setMode} /> : null}
      <Stack direction="row" gap={1} sx={{ alignItems: 'center' }}>
        {showBuilder ? (
          <Box sx={{ flex: 1 }}>
            <PromQLBuilder value={localValue} onChange={handleBuilderChange} hideCalculation hideRange hideQueryHints />
          </Box>
        ) : (
          <Box sx={(theme) => ({ flex: 1, backgroundColor: theme.palette.background.default })}>
            <PromQLEditor
              aria-label="Series selector"
              queryApiEndpoint={promURL}
              value={localValue}
              enableLinter={false}
              readOnly={isReadonly}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              width="100%"
              placeholder={'metric_name{label="value"}'}
              onChange={handleChange}
              onBlur={handleBlur}
              data-testid="series-selector-editor"
            />
          </Box>
        )}
        <IconButton aria-label="delete series selector" onClick={onDelete} disabled={isReadonly}>
          <DeleteIcon />
        </IconButton>
      </Stack>
      {localValue?.trim() === '' && (
        <Typography variant="caption" color="error">
          {"Series selector can't be blank."}
        </Typography>
      )}
    </Stack>
  );
}
